import type { ComponentSchema } from "../../../../../schema-types";
import { SECTION_STYLE_FIELDS } from "../../_builder-constants";

const FEATURE_ITEM_SCHEMA = [
  {
    key: "icon",
    label: "Icon",
    type: "select" as const,
    options: [
      { label: "Shipping Truck", value: "shipping" },
      { label: "Return Arrow", value: "refund" },
      { label: "Support Headset", value: "support" },
      { label: "Security Lock", value: "secure" },
      { label: "Gift Box", value: "gift" },
    ],
    default: "shipping",
  },
  { key: "title", label: "Title", type: "string" as const },
  { key: "subtitle", label: "Subtitle", type: "string" as const },
];

/** "Stat row": a big value over a small caption. */
const STAT_ITEM_SCHEMA = [
  { key: "title", label: "Value", type: "localized-string" as const, help: "A number or a short word, e.g. 2024." },
  { key: "subtitle", label: "Caption", type: "localized-string" as const },
];

/** "Numbered cells": an icon, a bold title and a line; numbered 01, 02… in order. */
const CELL_ITEM_SCHEMA = [
  {
    key: "icon",
    label: "Icon",
    type: "select" as const,
    options: ["sparkle", "star", "heart", "check", "shield", "truck", "package", "store", "map-pin", "tag", "user", "clock"]
      .map(value => ({ label: value.replace("-", " "), value })),
    default: "sparkle",
  },
  { key: "title", label: "Title", type: "localized-string" as const },
  { key: "subtitle", label: "Line", type: "localized-text" as const },
];

export const schema: ComponentSchema = {
  type: "features",
  label: "Features Strip",
  description: "Trust-building feature strip showing shipping, returns, support, and security highlights.",
  supportedPages: ["home", "shop", "categories", "about", "contact"],
  variants: [
    {
      id: "v1",
      label: "V1 — Compact Horizontal Strip",
      fields: [
        { key: "items", label: "Feature Items", type: "array", itemSchema: FEATURE_ITEM_SCHEMA },
        {
          key: "bgStyle",
          label: "Background Style",
          type: "select",
          options: [
            { label: "Default (Light Gray)", value: "default" },
            { label: "Bordered", value: "bordered" },
            { label: "Transparent", value: "transparent" },
          ],
          default: "default",
        },
      ],
    },
    {
      id: "v2",
      label: "V2 — Spaced Cards",
      fields: [
        { key: "items", label: "Feature Items", type: "array", itemSchema: FEATURE_ITEM_SCHEMA },
        {
          key: "bgStyle",
          label: "Background Style",
          type: "select",
          options: [
            { label: "Default (White)", value: "default" },
            { label: "Bordered", value: "bordered" },
            { label: "Transparent", value: "transparent" },
          ],
          default: "default",
        },
      ],
    },
    {
      id: "v3",
      label: "V3 — Jewelry Minimal Centered",
      fields: [
        { key: "items", label: "Feature Items", type: "array", itemSchema: FEATURE_ITEM_SCHEMA },
      ],
    },
    {
      id: "v4",
      label: "Stat row",
      fields: [
        {
          key: "items",
          label: "Figures",
          type: "array",
          itemSchema: STAT_ITEM_SCHEMA,
          default: [
            { title: "2024", subtitle: "The first drop" },
            { title: "Dhaka", subtitle: "Where we pack and ship" },
            { title: "40+", subtitle: "Styles in stock" },
            { title: "7 days", subtitle: "To swap a size" },
          ],
        },
      ],
    },
    {
      id: "v5",
      label: "Numbered cells on a black band",
      fields: [
        { key: "eyebrow", label: "Eyebrow", type: "localized-string", default: "Why people pick us" },
        { key: "title", label: "Heading", type: "localized-string", default: "Made with" },
        { key: "highlight", label: "Highlighted words", type: "localized-string", default: "fit the first time.", help: "Shown on the accent fill on their own line under the heading." },
        { key: "text", label: "Line beside the heading", type: "localized-text", default: "Every piece starts with the street it is worn on: what people wear, how they move and what they want to say." },
        {
          key: "items",
          label: "Cells",
          type: "array",
          itemSchema: CELL_ITEM_SCHEMA,
          default: [
            { icon: "sparkle", title: "Honest sizing", subtitle: "EU sizes and a fit note on every product." },
            { icon: "star", title: "New every week", subtitle: "Fresh pairs and colourways every Friday." },
            { icon: "shield", title: "Built to last", subtitle: "Heavy cotton and double stitching that keep their shape." },
            { icon: "heart", title: "Fair prices", subtitle: "Good shoes you can wear every day." },
          ],
        },
      ],
    },
  ],
  commonFields: [...SECTION_STYLE_FIELDS],
};
